import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'

// The one-line command that connects an agent to the MCP server. Reading
// which Google identity is allowed to drive it comes from users.email (set
// below via PUT /api/auth/email), not from anything in this command — the
// command itself is the same for everyone, which is why it can be a plain
// constant rather than templated per-campaign the way the old SKILL.md was.
const MCP_CONNECT_COMMAND = 'claude mcp add --transport http quest https://startyour.quest/mcp'

function McpPanel() {
  const { authEpoch } = useAuth()

  const [mcpEmail, setMcpEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState('idle') // idle | saving | saved | error
  const [emailError, setEmailError] = useState('')
  // Separate from emailStatus on purpose: emailStatus tracks the SAVE
  // request, this tracks the initial LOAD. Save must stay disabled until this
  // is 'loaded' — see loadEmail below for why a failed/incomplete load must
  // not leave the field looking like a legitimate empty value.
  const [emailLoadStatus, setEmailLoadStatus] = useState('loading') // loading | loaded | error
  const [commandCopied, setCommandCopied] = useState(false)

  // authEpoch: see the identical comment in components/tabs/ItemsTab.jsx.
  useEffect(() => {
    loadEmail()
  }, [authEpoch])

  // Prefills the email field from the caller's own saved address so the box
  // isn't blank by default. Blank-by-default was a footgun: PUT /api/auth/email
  // treats an empty string as "clear it", so a blank field next to a Save
  // button meant opening Settings and clicking Save for an unrelated reason
  // could silently revoke MCP access. Prefilling makes clearing it deliberate.
  //
  // A failed/non-ok load must NOT be allowed to look identical to a
  // genuinely-empty saved address — that's the exact footgun this was added
  // to close, just moved one step earlier: a blank field next to a Save
  // button that's still enabled would submit {email: ''} and silently revoke
  // access. emailLoadStatus tracks that distinction; the Save button below
  // stays disabled until it reads 'loaded'.
  const loadEmail = async () => {
    setEmailLoadStatus('loading')
    try {
      const res = await fetch('/api/auth/email')
      if (!res.ok) {
        setEmailLoadStatus('error')
        return
      }
      const data = await res.json()
      setMcpEmail(data.email || '')
      setEmailLoadStatus('loaded')
    } catch {
      setEmailLoadStatus('error')
    }
  }

  // /mcp verifies a Cloudflare Access (Google) sign-in and looks the caller
  // up by this column, so whatever address is saved here is the Google
  // identity allowed to drive an agent against this account. Prefilled by
  // loadEmail() above via GET /api/auth/email, so submitting this form
  // without touching the field re-saves the same address rather than
  // clearing it.
  const handleSaveEmail = async (e) => {
    e.preventDefault()
    setEmailStatus('saving')
    setEmailError('')
    try {
      const res = await fetch('/api/auth/email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: mcpEmail.trim() }),
      })
      const data = await res.json()
      if (!res.ok) {
        setEmailStatus('error')
        setEmailError(data.error || 'Could not save your email.')
        return
      }
      setEmailStatus('saved')
      // Otherwise the button reads "Saved" forever once shown — revert to
      // idle after a beat, same pattern as commandCopied below. A functional
      // update so this no-ops if the field has already moved on (e.g. the
      // user started typing again, which sets 'idle' itself).
      setTimeout(() => setEmailStatus((s) => (s === 'saved' ? 'idle' : s)), 2000)
    } catch {
      setEmailStatus('error')
      setEmailError('Could not save your email.')
    }
  }

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText(MCP_CONNECT_COMMAND)
      setCommandCopied(true)
      setTimeout(() => setCommandCopied(false), 2000)
    } catch {
      setCommandCopied(false)
    }
  }

  return (
    <section>
      <h2>Agent Access (MCP)</h2>
      <p className="ss-panel-hint">
        Connect a coding agent (like Claude) to the campaigns you DM. It can
        read and add content there, always hidden from players until you
        publish it here.
      </p>


      <div className="sp-setup">
        <div className="sp-setup-body" style={{ paddingTop: '1rem' }}>
          <h3 className="sp-mcp-heading" style={{ marginTop: 0 }}>Connect an agent</h3>

          <form onSubmit={handleSaveEmail} className="ss-form">
            <div className="ss-field">
              <label htmlFor="mcp-email">Your Google sign-in email</label>
              <input
                type="email"
                id="mcp-email"
                className="ss-input"
                value={mcpEmail}
                onChange={(e) => {
                  setMcpEmail(e.target.value)
                  setEmailStatus('idle')
                }}
                placeholder={emailLoadStatus === 'loading' ? 'Loading…' : 'you@example.com'}
                disabled={emailLoadStatus !== 'loaded'}
              />
              <p className="ss-panel-hint">
                Must match the Google account you sign in with when the MCP
                server asks you to authenticate. Leave this blank and save to
                revoke access.
              </p>
            </div>

            {emailLoadStatus === 'error' && (
              <p className="sp-error">
                Could not load your saved email, so saving is disabled — a
                blank field here would clear it.{' '}
                <button
                  type="button"
                  className="ss-btn ss-btn-neutral"
                  onClick={loadEmail}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Retry
                </button>
              </p>
            )}

            {emailStatus === 'error' && <p className="sp-error">{emailError}</p>}

            <button
              type="submit"
              className="ss-btn ss-btn-primary"
              disabled={emailStatus === 'saving' || emailLoadStatus !== 'loaded'}
            >
              {emailStatus === 'saving'
                ? 'Saving…'
                : emailStatus === 'saved'
                ? 'Saved'
                : emailLoadStatus === 'loading'
                ? 'Loading…'
                : 'Save email'}
            </button>
          </form>

          <p className="ss-panel-hint" style={{ marginTop: '1.25rem' }}>
            Then, in your terminal, run:
          </p>
          <pre className="sp-setup-snippet">{MCP_CONNECT_COMMAND}</pre>
          <div className="sp-setup-actions">
            <button
              type="button"
              className="ss-btn ss-btn-neutral"
              onClick={handleCopyCommand}
            >
              {commandCopied ? 'Copied' : 'Copy command'}
            </button>
          </div>
        </div>
      </div>

    </section>
  )
}

export default McpPanel
